import type { TicketFeeMode } from '@pulsify/shared'
import type {
  EventTicketSummary,
  TicketEarnings,
  TicketOrderCheckIn,
  TicketOrderListItem,
  TicketOrderListStatus,
  TicketOrderSort,
  TicketOrdersPage,
  TicketSettings,
  TicketType,
  ticketTypeInputSchema,
} from '@pulsify/shared/dto'
import type { z } from 'zod'
import { api, unwrap } from '~/shared/lib/rpc'

export type TicketTypeInput = z.input<typeof ticketTypeInputSchema>
export type TicketAttendee = TicketOrderListItem['tickets'][number]

export interface TicketOrdersFilter {
  search?: string
  typeId?: string
  status?: TicketOrderListStatus
  checkIn?: TicketOrderCheckIn
  sort: TicketOrderSort
}

export interface TicketOrdersQuery extends TicketOrdersFilter {
  page: number
}

function filterQuery(filter: TicketOrdersFilter) {
  return {
    sort: filter.sort,
    ...(filter.search ? { search: filter.search } : {}),
    ...(filter.typeId ? { typeId: filter.typeId } : {}),
    ...(filter.status ? { status: filter.status } : {}),
    ...(filter.checkIn ? { checkIn: filter.checkIn } : {}),
  }
}

const tickets = () => api.api.businesses[':businessId'].tickets

export const ticketsApi = {
  async settings(businessId: string): Promise<TicketSettings> {
    const res = await tickets().settings.$get({ param: { businessId } })
    return unwrap<TicketSettings>(res)
  },

  async setFeeMode(businessId: string, feeMode: TicketFeeMode): Promise<TicketSettings> {
    const res = await tickets().settings.$put({
      param: { businessId },
      json: { feeMode },
    })
    return unwrap<TicketSettings>(res)
  },

  async onboardingLink(
    businessId: string,
    urls: { returnUrl: string; refreshUrl: string },
  ): Promise<string> {
    const res = await tickets().connect.onboarding.$post({
      param: { businessId },
      json: urls,
    })
    return (await unwrap<{ url: string }>(res)).url
  },

  async refreshConnect(businessId: string): Promise<TicketSettings> {
    const res = await tickets().connect.refresh.$post({ param: { businessId } })
    return unwrap<TicketSettings>(res)
  },

  async types(businessId: string, eventId: string): Promise<TicketType[]> {
    const res = await tickets().events[':eventId'].types.$get({
      param: { businessId, eventId },
    })
    return (await unwrap<{ types: TicketType[] }>(res)).types
  },

  async createType(
    businessId: string,
    eventId: string,
    input: TicketTypeInput,
  ): Promise<TicketType> {
    const res = await tickets().events[':eventId'].types.$post({
      param: { businessId, eventId },
      json: input,
    })
    return unwrap<TicketType>(res)
  },

  async updateType(
    businessId: string,
    typeId: string,
    input: Partial<TicketTypeInput>,
  ): Promise<TicketType> {
    const res = await tickets().types[':typeId'].$patch({
      param: { businessId, typeId },
      json: input,
    })
    return unwrap<TicketType>(res)
  },

  async deleteType(businessId: string, typeId: string): Promise<void> {
    const res = await tickets().types[':typeId'].$delete({
      param: { businessId, typeId },
    })
    await unwrap<void>(res)
  },

  async summary(businessId: string, eventId: string): Promise<EventTicketSummary> {
    const res = await tickets().events[':eventId'].summary.$get({
      param: { businessId, eventId },
    })
    return unwrap<EventTicketSummary>(res)
  },

  async ordersPage(
    businessId: string,
    eventId: string,
    query: TicketOrdersQuery,
  ): Promise<TicketOrdersPage> {
    const res = await tickets().events[':eventId'].orders.page.$get({
      param: { businessId, eventId },
      query: { ...filterQuery(query), page: String(query.page) },
    })
    return unwrap<TicketOrdersPage>(res)
  },

  async exportOrders(businessId: string, eventId: string, filter: TicketOrdersFilter) {
    const res = await tickets().events[':eventId'].orders.export.$get({
      param: { businessId, eventId },
      query: filterQuery(filter),
    })
    if (!res.ok) await unwrap<never>(res)
    return res.blob()
  },

  async refund(businessId: string, orderId: string): Promise<void> {
    const res = await tickets().orders[':orderId'].refund.$post({
      param: { businessId, orderId },
    })
    await unwrap<unknown>(res)
  },

  async earnings(businessId: string, hostProfileId?: string): Promise<TicketEarnings> {
    const res = await tickets().earnings.$get({
      param: { businessId },
      query: hostProfileId ? { hostProfileId } : {},
    })
    return unwrap<TicketEarnings>(res)
  },

  async voidTicket(businessId: string, ticketId: string): Promise<void> {
    const res = await tickets().tickets[':ticketId'].void.$post({
      param: { businessId, ticketId },
    })
    await unwrap<void>(res)
  },
}
